import { Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { FaqCategoria } from '../faq.types';
import { accentChipStyle } from '../lib/faq-format';
import { FaqCategoryIcon } from './faq-category-icon';

interface FaqFiltersProps {
  query: string;
  onQueryChange: (value: string) => void;
  categorias: readonly FaqCategoria[];
  categoriaId: number | null;
  onCategoriaChange: (id: number | null) => void;
  totalItems: number;
}

/**
 * Buscador + filtro por categoría del FAQ.
 *
 * Los filtros viven en la página (no en la URL) porque esta vista es de consulta
 * rápida y no necesita enlaces compartibles.
 */
export function FaqFilters({
  query,
  onQueryChange,
  categorias,
  categoriaId,
  onCategoriaChange,
  totalItems,
}: FaqFiltersProps) {
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 size-4.5 -translate-y-1/2 text-ink-400"
        />
        <input
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Buscar en las preguntas frecuentes… (ej. factura, sunat, turno)"
          aria-label="Buscar en las preguntas frecuentes"
          className={cn(
            'h-12 w-full rounded-control border border-ink-200 bg-white pl-11 pr-11 text-sm text-ink-900',
            'placeholder:text-ink-400 transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
          )}
        />

        {query !== '' ? (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            aria-label="Limpiar búsqueda"
            className="absolute right-3 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-600"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-pressed={categoriaId === null}
          onClick={() => onCategoriaChange(null)}
          className={cn(
            'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
            categoriaId === null
              ? 'border-brand-300 bg-brand-50 text-brand-700'
              : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-600',
          )}
        >
          Todas
          <span className="rounded-full bg-white/70 px-1.5 text-[11px] font-semibold text-ink-500">
            {totalItems}
          </span>
        </button>

        {categorias.map((categoria) => {
          const activa = categoria.id === categoriaId;

          return (
            <button
              key={categoria.id}
              type="button"
              aria-pressed={activa}
              onClick={() => onCategoriaChange(activa ? null : categoria.id)}
              title={categoria.descripcion ?? categoria.nombre}
              className={cn(
                'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                activa
                  ? 'bg-white font-semibold'
                  : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-600',
              )}
              style={activa ? accentChipStyle(categoria.color) : undefined}
            >
              <FaqCategoryIcon
                icono={categoria.icono}
                color={categoria.color}
                className="size-5 rounded-md text-[11px]"
              />
              {categoria.nombre}
              <span className="rounded-full bg-white/70 px-1.5 text-[11px] font-semibold text-ink-500">
                {categoria.total_items}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
